import React, { useRef, useState } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

const TARGET_WIDTH = 300;
const TARGET_HEIGHT = 280;

const ImageCropUpload = ({
  name,
  setFieldValue,
  setFieldTouched,
  onCroppedFile,
  onBlur,
  error,
  touched,
}) => {
  const cropperRef = useRef(null);
  const [image, setImage] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("image/jpeg");
  const [isCropReady, setIsCropReady] = useState(false);

  const dataUrlToFile = (dataUrl) => {
    const [metadata, data] = dataUrl.split(",");
    const mime = metadata.match(/:(.*?);/)?.[1] || "image/jpeg";
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    const extension = mime === "image/png" ? "png" : "jpg";
    return new File(
      [bytes],
      fileName || `cropped-${TARGET_WIDTH}x${TARGET_HEIGHT}.${extension}`,
      { type: mime }
    );
  };

  const updateCroppedImage = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return null;

    const canvas = cropper.getCroppedCanvas({
      width: TARGET_WIDTH,
      height: TARGET_HEIGHT,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });

    if (!canvas) return null;

    const croppedFile = dataUrlToFile(
      canvas.toDataURL(fileType === "image/png" ? "image/png" : "image/jpeg", 0.92)
    );
    setFieldValue(name, croppedFile);
    setIsCropReady(true);
    if (onCroppedFile) onCroppedFile(croppedFile);

    return croppedFile;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setFieldTouched(name, true, false);

    if (!file) {
      setImage("");
      setFieldValue(name, null);
      setIsCropReady(false);
      return;
    }

    setFileName(file.name);
    setFileType(file.type || "image/jpeg");
    setFieldValue(name, null);
    setIsCropReady(false);

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <input
        type="file"
        name={name}
        className="form-control set-to-auto-height"
        accept="image/png, image/jpeg, image/jpg, image/bmp, image/webp"
        onBlur={onBlur}
        onChange={handleFileChange}
      />
      {image && (
        <div className="mt-3">
          <Cropper
            ref={cropperRef}
            src={image}
            style={{ height: 280, width: "100%" }}
            aspectRatio={TARGET_WIDTH / TARGET_HEIGHT}
            viewMode={1}
            background={false}
            responsive
            autoCropArea={1}
            checkOrientation={false}
            guides
            ready={updateCroppedImage}
            cropend={updateCroppedImage}
            zoom={updateCroppedImage}
          />
          <div className="d-flex align-items-center justify-content-between mt-2">
            <small className={isCropReady ? "text-success" : "text-muted"}>
              {isCropReady
                ? "Image ready: 300 x 280 crop will be uploaded."
                : "Adjust the crop, then apply it before submitting."}
            </small>
            <button
              type="button"
              className="btn btn-primary outline-btn-cls"
              onClick={updateCroppedImage}
            >
              Apply crop
            </button>
          </div>
          <small className="text-muted">
            Drag or zoom the image. The final upload is saved as 300 x 280.
          </small>
        </div>
      )}
      {error && touched ? <p className="form-error">{error}</p> : null}
    </>
  );
};

export default ImageCropUpload;
